import html2canvas from "html2canvas";

/**
 * Download card as image using html2canvas with high quality
 * @param {string} elementId - ID of the element to capture
 * @param {string} filename - Name of the file to download
 * @param {number} scale - Scale factor for image quality (default 2)
 */
export const downloadCardAsImage = async (elementId, filename = "my-horoscope.png", scale = 2) => {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error("Element not found");
    }

    const canvas = await html2canvas(element, {
      backgroundColor: "#000000",
      scale: scale,
      useCORS: true,
      allowTaint: true,
      logging: false,
    });

    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error downloading card:", error);
    throw new Error("Failed to download card. Try again! 🌟");
  }
};

/**
 * Convert canvas to blob for web sharing
 * @param {string} elementId - ID of the element to capture
 * @param {number} scale - Scale factor for image quality
 * @returns {Promise<Blob>} Image blob for sharing
 */
export const generateCardAsBlob = async (elementId, scale = 2) => {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error("Element not found");
    }

    const canvas = await html2canvas(element, {
      backgroundColor: "#000000",
      scale: scale,
      useCORS: true,
      allowTaint: true,
      logging: false,
    });

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create blob"));
        }
      }, "image/png");
    });
  } catch (error) {
    console.error("Error generating blob:", error);
    throw error;
  }
};

/**
 * Share card image using Web Share API
 * @param {Blob} imageBlob - Image blob to share
 * @param {string} platform - Platform name (Instagram, TikTok, etc.)
 */
export const shareImageWithPlatform = async (imageBlob, platform = "Image") => {
  try {
    if (!navigator.share) {
      throw new Error("Web Share API not supported");
    }

    const file = new File([imageBlob], `horoscope.png`, { type: "image/png" });

    // Check if the browser supports sharing files
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: "My WTFHoroscope",
        text: `Check out my WTFHoroscope reading! 💀✨`,
      });
    } else {
      throw new Error("File sharing not supported on this browser");
    }
  } catch (error) {
    console.error("Error sharing image:", error);
    throw error;
  }
};

/**
 * Copy prediction text to clipboard
 * @param {string} text - Text to copy
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error("Error copying to clipboard:", error);
    return false;
  }
};

/**
 * Share via Web Share API (mobile)
 * @param {string} title - Share title
 * @param {string} text - Share text
 * @param {string} url - Share URL
 */
export const shareViaWebShareAPI = async (title, text, url = window.location.href) => {
  if (!navigator.share) {
    throw new Error("Web Share API not supported on this device");
  }

  try {
    await navigator.share({
      title,
      text,
      url,
    });
    return true;
  } catch (error) {
    if (error.name !== "AbortError") {
      console.error("Error sharing:", error);
    }
    return false;
  }
};

/**
 * Share via WhatsApp
 * @param {string} message - Message to share
 */
export const shareViaWhatsApp = (message) => {
  const encodedMessage = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
  window.open(whatsappUrl, "_blank");
};

/**
 * Share via Twitter/X
 * @param {string} message - Message to share
 */
export const shareViaTwitter = (message) => {
  const encodedMessage = encodeURIComponent(message);
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedMessage}&url=${encodeURIComponent(window.location.href)}`;
  window.open(twitterUrl, "_blank");
};

/**
 * Generate share text for horoscope
 * @param {string} name - User's name
 * @param {string} zodiacName - Zodiac sign name
 * @param {string} prediction - Prediction text or bullet points
 * @returns {string} Formatted share text
 */
export const generateShareText = (name, zodiacName, prediction) => {
  return `${name}'s ${zodiacName} horoscope just called them out 💀

${prediction}

Get your brutally honest horoscope at WTFHoroscope.com 🔥
#WTFHoroscope #${zodiacName}Season #Horoscope #Astrology`;
};

/**
 * Generate caption for social sharing
 * @param {string} name - User's name
 * @param {string} zodiacName - Zodiac sign name
 * @returns {string} Formatted caption
 */
export const generateCaption = (name, zodiacName) => {
  return `${name}'s ${zodiacName} horoscope just called them out 💀
Try yours at WTFHoroscope.com 
#WTFHoroscope #${zodiacName}Season #Horoscope #Astrology`;
};

/**
 * Generate challenge message for WhatsApp
 * @param {string} name - User's name
 * @returns {string} Challenge message
 */
export const generateChallengeMessage = (name) => {
  return `Bro I just got roasted by my horoscope 💀\nTry yours: wtfhoroscope.com`;
};

/**
 * Generate random reaction count (daily based on date seed)
 * @returns {number} Random count between 2000-5000
 */
export const generateReactionCount = () => {
  const today = new Date();
  const dateString = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  
  // Simple hash function for consistent daily numbers
  let hash = 0;
  for (let i = 0; i < dateString.length; i++) {
    const char = dateString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return Math.abs(hash % 3001) + 2000; // Range: 2000-5000
};

/**
 * Check if Web Share API is supported
 * @returns {boolean}
 */
export const isWebShareSupported = () => {
  return !!navigator.share;
};

/**
 * Check if Web Share API supports files
 * @returns {boolean}
 */
export const isFileShareSupported = () => {
  return !!(navigator.share && navigator.canShare);
};

/**
 * Check if the app is on mobile
 * @returns {boolean}
 */
export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};
